import {
  xviCustomGridRenderers,
  xviKeyboardAutoFocusGridComponents,
} from '@app/components/xvi-grid/custom-components';
import {
  clearFocusedCellRenderer,
  getCellRendererFrameworkInstance,
  getCellRendererInstance,
} from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { Column, CellPosition, GridApi } from 'ag-grid-community';
import {
  isEmpty,
  isNil,
  some,
  isEqual,
  isFunction,
  debounce,
  assign,
  pick,
  map,
  compact,
  get,
} from 'lodash';
import { AUTO_FOCUS_DELAY } from './keyboard-navigation.config';

import {
  IAgGridNavigationOptions,
  IEditableColumn,
  NavigationHorizontalDirection,
  NavigationVerticalDirection,
} from './keyboard-navigation.types';

/**
 * Navigation utility class
 */
export class NavigationUtils {
  /**
   * Get next row index
   */
  public static getNextRowIndex(
    currentIndex: number,
    direction: NavigationVerticalDirection,
    rowCount: number,
  ): number | null {
    if (isEqual(direction, NavigationVerticalDirection.UP)) {
      return currentIndex > 0 ? currentIndex - 1 : null;
    }

    return currentIndex < rowCount - 1 ? currentIndex + 1 : null;
  }

  /**
   * Get next column index
   */
  public static getNextColumnIndex(
    currentIndex: number,
    direction: NavigationHorizontalDirection,
    columnCount: number,
    options: { wrap: boolean } = { wrap: false },
  ): number | null {
    const isLeft = isEqual(direction, NavigationHorizontalDirection.LEFT);
    const nextIndex = currentIndex + (isLeft ? -1 : 1);
    const hasNext = isLeft ? currentIndex > 0 : currentIndex < columnCount - 1;

    if (hasNext) return nextIndex;

    if (!options.wrap) return null;

    return isLeft ? columnCount - 1 : 0;
  }

  /**
   * is column editable
   * Two conditions:
   * 1. column is editable
   * 2. column is visible
   *
   * @param column
   * @returns
   */
  public static isEditableColumn(column: Column): boolean {
    if (!column || !column.isVisible()) return false;

    const colDef = column.getColDef();

    return isFunction(colDef?.editable)
      ? colDef.editable({
          column,
          colDef,
          api: get(column, ['gridApi']),
          columnApi: get(column, ['gridApi', 'rowModel', 'columnApi']),
          context: get(column, ['gridApi', 'context']),
          node: null,
          data: null,
        })
      : colDef?.editable ?? false;
  }

  /**
   * Determine if the cell is editable
   */
  public static isEditableCell(
    column: Column,
    rowIndex: number,
    gridApi: GridApi,
    options: IAgGridNavigationOptions,
  ): boolean {
    if (!NavigationUtils.isEditableColumn(column)) {
      return false;
    }

    const rowData = gridApi.getModel().getRow(rowIndex);
    if (!rowData?.data) return false;

    // Custom check
    if (isFunction(options.customEditableCheck)) {
      return options.customEditableCheck(column, rowIndex, rowData.data);
    }

    // all editable render definitions, u should add your custom editable renderers here to support keyboard navigation
    const editableCellRenderers = assign(
      {},
      xviKeyboardAutoFocusGridComponents,
      pick(xviCustomGridRenderers, ['customSelectRenderer']),
    );

    const frameworkComponentInstance = getCellRendererFrameworkInstance({
      rowIndex,
      column,
      gridApi,
    });

    return some(
      editableCellRenderers,
      (cellRenderer) => frameworkComponentInstance instanceof cellRenderer,
    );
  }

  /**
   * Find editable columns in the row
   */
  public static findEditableColumns(
    rowIndex: number,
    columns: Column[],
    gridApi: GridApi,
    options: IAgGridNavigationOptions,
  ): IEditableColumn[] {
    const columnsWithRowIndex = map(columns, (column, index) =>
      this.isEditableCell(column, rowIndex, gridApi, options)
        ? { column, indexInRow: index }
        : null,
    );

    return compact(columnsWithRowIndex);
  }

  /**
   * Create cell position object
   */
  public static createCellPosition(
    rowIndex: number,
    column: Column,
    rowPinned?: string,
  ): CellPosition {
    return {
      rowIndex,
      column,
      rowPinned: rowPinned || null,
    };
  }

  /**
   * Validate if cell position is valid
   */
  public static isValidCellPosition(
    position: CellPosition | null | undefined,
  ): position is CellPosition {
    return (
      position != null && position.rowIndex != null && position.column != null
    );
  }

  public static focusCellInnerElement(params: {
    rowIndex: number;
    column: Column;
    api: GridApi;
  }): void {
    const { rowIndex, column, api: gridApi } = params;

    if (!gridApi) {
      return;
    }

    const focusedCellRendererInstance = getCellRendererInstance({
      rowIndex,
      columnId: column.getId(),
      gridApi,
    });

    const frameworkComponentInstance = getCellRendererFrameworkInstance({
      rowIndex,
      column,
      gridApi,
    });

    if (
      !isNil(focusedCellRendererInstance) &&
      some(
        xviKeyboardAutoFocusGridComponents,
        (component) => frameworkComponentInstance instanceof component,
      )
    ) {
      const inputEl: HTMLElement = focusedCellRendererInstance
        .getGui()
        .querySelector('input');

      if (
        !isEqual(document.activeElement, inputEl) &&
        !isEqual(document.activeElement.parentElement, inputEl?.parentElement)
      ) {
        inputEl?.focus();
      }
    }
  }

  /**
   * Debounce to avoid calling focus multiple times when user quickly navigate through cells,
   * which will cause performance issue and unexpected focus behavior
   */
  public static debounceFocusCellInnerElement = debounce(
    (event) => NavigationUtils.focusCellInnerElement(event),
    AUTO_FOCUS_DELAY,
  );

  /**
   * re-focus the currently focused cell
   *
   * @param params
   * @returns
   */
  public static reFocusCell(params: { gridApi: GridApi }): void {
    const { gridApi } = params;

    const focusedCell = gridApi.getFocusedCell();

    if (!isEmpty(focusedCell?.rowPinned)) return;

    if (focusedCell) {
      gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
    }
  }

  /**
   * clear focused cell, for example, when user click on the blank area of the grid,
   * we want to clear the focused cell but not set it to another cell,
   * which will cause unexpected focus state
   *
   * @param params
   */
  public static clearFocusedCell(params: { gridApi: GridApi }): void {
    const { gridApi } = params;

    clearFocusedCellRenderer({ gridApi });

    gridApi?.clearFocusedCell();
  }
}
